// The Matches widget's live adapter (Track 2, T2-11): app-api → the same shapes the snapshot
// gives the timeline (appearances, results, goal timelines, shots + passes by match).
//
// Every club season, in whichever league she played it (Ben, 2026-10-26: Rodri's Manchester City
// seasons were missing — it used to be her current league only, Prateek 2026-10-12). The seasons
// come from her career (`/player-career`, the Bio's Career screen), then one season's five answers together
// (appearances, the league's matches, her matches' goals, her shots, her passes). Everything
// loads with the season, as the snapshot had it (option A: no loading by chip — Track 3).

import type { PlayerStatsResponse, PositionsResponse } from "@catbento/shared";
import { minutesByPositionRequest, positionMinutesOf } from "./bio.js";
import type {
  MatchSummary,
  PlayerAppearance,
  PlayerAppearancesResponse,
  ProtoAppearance,
  ProtoAppearancePosition,
  ProtoEvent,
  ProtoFixture,
} from "@catbento/shared";
import type { CompetitionId } from "../data/league.js";
import type { PlayerId } from "../data/players.js";
import type { MatchGoalsView } from "../modules/matches-view.js";
import type { LiveApi } from "./api.js";
import type { CareerSeason } from "./career.js";
import { EVENT_KINDS, fetchEventSeason, type RawEventSeason } from "./event-map.js";
import { toProtoFixtures } from "./results.js";

/** One club season in the season list: its league and club. */
export interface MatchesSeason {
  seasonCode: string;
  competitionId: CompetitionId;
  teamName: string;
}

/**
 * Her club seasons, newest first, each with the league she played it in (Ben, 2026-10-26: a move
 * abroad must not hide the seasons before it). The same rule as `/player-current-league` for a
 * season (app-api db/current-league.ts): her club in her LATEST domestic match that season, then
 * that club's domestic competition with the most minutes (a league outweighs a cup). Domestic = a
 * real country that isn't "International". A season with only cup-of-Europe or national-team
 * games has no league, so it isn't listed. The first is her current season. Pure.
 */
export function matchesSeasons(career: readonly CareerSeason[]): MatchesSeason[] {
  const out: MatchesSeason[] = [];
  for (const s of career) {
    const domestic = s.lines.filter((l) => l.league?.countryName && l.league.countryName !== "International");
    const latest = domestic.reduce<(typeof domestic)[number] | null>((a, l) => (a === null || l.lastMatchDate > a.lastMatchDate ? l : a), null);
    if (latest === null) continue;
    const league = domestic.filter((l) => l.teamId === latest.teamId).reduce((a, l) => (l.minutes > a.minutes ? l : a));
    out.push({ seasonCode: s.seasonCode, competitionId: league.competitionId, teamName: league.teamName });
  }
  // careerSeasons is oldest first.
  return out.reverse();
}

/** The pipeline's `position_durations` entry: `{ position, duration }`, the snapshot's own shape.
 *  Anything else is a shape change → throws. */
function toPositions(raw: unknown, matchId: number): ProtoAppearancePosition[] {
  // Null = no position recorded: a game on the bench (the snapshot lists those with []).
  if (raw === null) return [];
  if (!Array.isArray(raw)) throw new Error(`Appearance in match ${matchId}: position_durations isn't a list`);
  return raw.map((p: unknown) => {
    const { position, duration } = (p ?? {}) as { position?: unknown; duration?: unknown };
    if (typeof position !== "string" || typeof duration !== "number") {
      throw new Error(`Appearance in match ${matchId}: unexpected position entry ${JSON.stringify(p)}`);
    }
    return { position, duration };
  });
}

/** The pipeline's `appearance_metrics.key_events`: `{ type, minute, … }` per event (Catalyst reads
 *  goals and yellow cards from it). No metrics / no list → none; any other shape → throws. */
export function toKeyEvents(raw: unknown, matchId: number): { type: string; minute: number }[] {
  if (raw === null || raw === undefined) return [];
  if (typeof raw !== "object") throw new Error(`Appearance in match ${matchId}: appearance_metrics isn't an object`);
  const list = (raw as { key_events?: unknown }).key_events;
  if (list === undefined || list === null) return [];
  if (!Array.isArray(list)) throw new Error(`Appearance in match ${matchId}: key_events isn't a list`);
  return list.map((e: unknown) => {
    const { type, minute } = (e ?? {}) as { type?: unknown; minute?: unknown };
    if (typeof type !== "string" || typeof minute !== "number") {
      throw new Error(`Appearance in match ${matchId}: unexpected key event ${JSON.stringify(e)}`);
    }
    return { type, minute };
  });
}

const minuteText = (n: number | null) => (n === null ? null : String(n));

/** One API appearance → the snapshot's. Minutes become strings (the snapshot's own type, the
 *  renderer Number()s them); a game on the bench keeps its nulls, as in the snapshot. */
export function toProtoAppearance(a: PlayerAppearance): ProtoAppearance {
  if (a.minutesOnPitch === null) throw new Error(`Appearance in match ${a.matchId} has no minutes`);
  return {
    matchDate: a.matchDate,
    matchId: a.matchId,
    inStartingXi: a.inStartingXi,
    onPitchMinute: minuteText(a.onPitchMinute),
    onPitchPeriod: a.onPitchPeriod,
    offPitchMinute: minuteText(a.offPitchMinute),
    offPitchPeriod: a.offPitchPeriod,
    offPitchType: a.offPitchType,
    minutesOnPitch: a.minutesOnPitch,
    positions: toPositions(a.positionDurations, a.matchId),
    keyEvents: toKeyEvents(a.appearanceMetrics, a.matchId),
  };
}

/** Her team's NAME in each of her matches (by the appearance's team id) — the timeline names
 *  the opponent + H/A by comparing it with the fixture's teams, so a past season at another
 *  club still reads right. */
export function teamNamesByMatch(appearances: PlayerAppearance[], matches: MatchSummary[]): Map<number, string> {
  const byId = new Map(matches.map((m) => [m.matchId, m]));
  const out = new Map<number, string>();
  for (const a of appearances) {
    const m = byId.get(a.matchId);
    if (!m) continue; // not in the league's results: the row falls back to its date
    if (a.teamId === m.homeTeamId) out.set(a.matchId, m.homeTeamName);
    else if (a.teamId === m.awayTeamId) out.set(a.matchId, m.awayTeamName);
    else throw new Error(`Appearance in match ${a.matchId}: team ${a.teamId} plays in neither side`);
  }
  return out;
}

/** The goals in her matches → Team Goals' per-match records (side, team, minute, scorer, a
 *  penalty, an own goal: on the side it counts for, Ben 2026-10-30). */
export function toMatchGoalsView(goals: RawEventSeason["goals"], matches: MatchSummary[]): Record<string, MatchGoalsView> {
  const byId = new Map(matches.map((m) => [m.matchId, m]));
  const out: Record<string, MatchGoalsView> = {};
  for (const g of goals) {
    const m = byId.get(g.matchId);
    if (!m) continue; // no fixture → no sides to place it on
    if (g.minute === null || g.teamId === null || g.teamName === null || g.scorerName === null) {
      throw new Error(`Goal in match ${g.matchId} is missing its minute, team or scorer`);
    }
    const rec = (out[String(g.matchId)] ??= { homeTeam: m.homeTeamName, goals: [] });
    rec.goals.push({
      team: g.teamId === m.homeTeamId ? "home" : "away",
      teamName: g.teamName,
      minute: g.minute,
      period: g.period,
      scorer: g.scorerName,
      // Only "a penalty or not" is served; the other shot types aren't (and aren't shown).
      shotType: g.isOwnGoal ? "Own Goal" : g.isPenalty ? "Penalty" : null,
    });
  }
  return out;
}

function byMatch(events: ProtoEvent[]): Record<string, ProtoEvent[]> {
  const out: Record<string, ProtoEvent[]> = {};
  for (const e of events) (out[String(e.match_id)] ??= []).push(e);
  return out;
}

/** One season of the timeline. */
export interface LiveMatchesSeason {
  appearances: ProtoAppearance[];
  results: Record<string, ProtoFixture[]>;
  matchGoals: Record<string, MatchGoalsView>;
  shotsByMatch: Record<string, ProtoEvent[]>;
  passesByMatch: Record<string, ProtoEvent[]>;
  teamByMatch: Map<number, string>;
}

export async function loadMatchesSeason(
  api: LiveApi,
  playerId: PlayerId,
  competitionId: CompetitionId,
  seasonCode: string,
): Promise<LiveMatchesSeason> {
  const [{ appearances }, events] = await Promise.all([
    api.get<PlayerAppearancesResponse>(`/player-appearances?playerId=${playerId}&competitionId=${competitionId}&seasonId=${seasonCode}`),
    fetchEventSeason(api, EVENT_KINDS.both, playerId, competitionId, seasonCode),
  ]);
  return {
    appearances: appearances.map(toProtoAppearance),
    results: { [String(competitionId)]: toProtoFixtures(events.matches) },
    matchGoals: toMatchGoalsView(events.goals, events.matches),
    shotsByMatch: byMatch(events.shots),
    passesByMatch: byMatch(events.passes),
    teamByMatch: teamNamesByMatch(appearances, events.matches),
  };
}

/** Her minutes at each position in one league + season (named, most first) — the chips under the
 *  Matches chart (Ben, 2026-10-18: moved there from the Bio). */
export async function loadPositionMinutes(
  api: LiveApi,
  playerId: PlayerId,
  competitionId: CompetitionId,
  seasonCode: string,
): Promise<{ id: string; position: string; minutes: number }[]> {
  const [{ rows }, { positions }] = await Promise.all([
    api.post<PlayerStatsResponse>("/player-stats", minutesByPositionRequest(playerId, competitionId, Number(seasonCode))),
    api.get<PositionsResponse>("/positions"),
  ]);
  return positionMinutesOf(rows, positions);
}
